import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button } from "react-bootstrap";
import swal from "sweetalert";

import { updateCaseDetails } from "@services/case-details";
import { getCaseDocumentAnalyses } from "@services/document-analysis";
import { INCOME_TYPE } from "@constants/income-types";
import { DOCUMENT_LABELS, getDocumentChecklist, isMinorByBirthdate } from "./document-intake-prefill";
import { getRequiredDocuments } from "./document-intake-profiles";
import { IntakeHaloStyles } from "./intake-summary";
import { linkAnalysesToCase, useDocumentIntake } from "./use-document-intake";

/**
 * Recupera un caso que llego a Mesa de Control por documentacion incompleta (etiqueta
 * documentation_incomplete de tra_case_details): muestra que documentos faltan, deja subirlos y,
 * en cuanto el expediente esta completo, quita la etiqueta. Si los documentos ya se completaron por
 * otra via, la etiqueta se quita al abrir el caso. No mueve el caso de estacion: eso lo decide
 * quien lo gestiona. `onRecovered` avisa para recargar el caso.
 */
const DocumentationRecovery = ({ caseRecord, onRecovered }) => {
  const { requestDocumentList, intakeModal } = useDocumentIntake();
  const [analyses, setAnalyses] = useState(null);
  const uploadedNow = useRef(false);
  const clearing = useRef(false);

  const flagged = caseRecord.documentationIncomplete === true;
  const isReport = caseRecord.incomeType === INCOME_TYPE.HOSPITAL_REPORT.value;
  const minor = isMinorByBirthdate(caseRecord.assuredBrthdate);
  const checklist = analyses
    ? getDocumentChecklist(getRequiredDocuments(isReport, { minor }), analyses)
    : [];
  const missing = checklist.filter((document) => !document.loaded);
  const complete = analyses !== null && missing.length === 0;

  const refresh = useCallback(
    () =>
      getCaseDocumentAnalyses(caseRecord.slug).then((response) => {
        setAnalyses(response.data);
        return response.data;
      }),
    [caseRecord.slug]
  );

  useEffect(() => {
    if (flagged && caseRecord.slug) {
      refresh().catch(() => {});
    }
  }, [flagged, caseRecord.slug, refresh]);

  useEffect(() => {
    if (!flagged || !complete || clearing.current) {
      return;
    }
    clearing.current = true;
    updateCaseDetails(caseRecord.id, { documentationIncomplete: false })
      .then(() => {
        if (uploadedNow.current) {
          swal("Documentación completa", "El expediente ya tiene todos los documentos.", "success");
        }
        if (onRecovered) {
          onRecovered();
        }
      })
      .catch(() => {
        clearing.current = false;
      });
  }, [flagged, complete, caseRecord.id, onRecovered]);

  if (!flagged) {
    return null;
  }

  const uploadMissing = () =>
    requestDocumentList(
      missing.flatMap((document) =>
        document.types.map((type) => ({ type, label: DOCUMENT_LABELS[type], required: false }))
      ),
      async (uploaded) => {
        await linkAnalysesToCase(caseRecord.slug, uploaded);
        uploadedNow.current = uploaded.length > 0;
        await refresh().catch(() => {});
      },
      "Documentos faltantes del expediente"
    );

  return (
    <>
      <IntakeHaloStyles />
      <Alert variant="warning" className="mt-3">
        <div className="font-weight-bold mb-2">
          <i className="fa fa-exclamation-triangle mr-2" />
          Documentación incompleta
        </div>
        <div className="small mb-2">
          Este caso llegó a Mesa de Control sin todos los documentos del expediente. Al completarlos
          se quita esta marca.
        </div>
        <div>
          {checklist.map((document) => (
            <span key={document.label} className={`di-chip ${document.loaded ? "ok" : "missing"}`}>
              {document.loaded ? "✓" : "!"} {document.label}
            </span>
          ))}
        </div>
        <Button size="sm" variant="warning" className="mt-1" onClick={uploadMissing} disabled={missing.length === 0}>
          Subir documentos faltantes
        </Button>
      </Alert>
      {intakeModal}
    </>
  );
};

export default DocumentationRecovery;
